import { useCallback, useEffect, useRef, useState } from "react";
import type { Clinic } from "@dentacore/shared-types";
import { ALLOWED_LOGO_MIME_TYPES, MAX_LOGO_BYTES } from "@dentacore/shared-types";

import {
  confirmClinicLogo,
  presignClinicLogo,
  readClinic,
  removeClinicLogo,
  updateClinic,
} from "../api/clinic";
import { CountryPicker, CurrencyPicker, TimezonePicker } from "../ui/pickers";
import { RequiredMark } from "../ui/required-mark";
import { WorkingDays } from "../ui/working-days";
import { LogoHoopDialog } from "./logo-hoop/logo-hoop-dialog";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";

/**
 * The clinic's own identity — STORY-070, and the last of the admin screens.
 *
 * **Three fields are shown and cannot be edited**, each for its own reason: the
 * subdomain, because changing it breaks every bookmark and link the practice
 * has handed out; the currency once a price exists (decision L2), because it
 * converts nothing and would silently change what every stored amount *means*,
 * invoices included; and the locale, which is a provisioning input no screen
 * asks for.
 *
 * **None of those reasons is on screen any more** (UI-P2, 2026-09-22). They
 * each had a line under the field saying why, and the client had every line of
 * explanatory prose removed from Settings. The reasons are recorded here and in
 * `DECISIONS.md` instead, so a later session does not read a dead field as a
 * bug and "fix" it.
 *
 * **The timezone is editable and is not retroactive**: `branches.timezone` is
 * required per branch rather than inherited, so this seeds new locations and
 * moves nothing already created.
 */
export function ClinicSection(): React.JSX.Element {
  const { busy, toast, dismiss, fail, run } = useWriter();

  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [editing, setEditing] = useState(false);
  /** The logo file the admin picked and has not finished uploading (STORY-179). */
  const [pending, setPending] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(async (signal?: AbortSignal) => {
    setClinic(await readClinic(signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read this clinic's details."));
    });

    return () => controller.abort();
  }, [reload, fail]);

  /**
   * Presign, `PUT`, confirm — the attachment flow (STORY-025), unchanged.
   *
   * The `PUT` goes straight to storage with **no bearer token**: the signature
   * is in the URL, and sending our own token to a third-party host would leak
   * it. That is why it is a bare `fetch` and not the API client.
   */
  async function upload(file: File): Promise<void> {
    const signed = await presignClinicLogo(file.type, file.size);

    const response = await fetch(signed.uploadUrl, {
      method: "PUT",
      body: await file.arrayBuffer(),
      headers: { "content-type": file.type },
    });

    if (!response.ok) {
      throw new Error(
        `Storage refused the upload (${String(response.status)}). The link may have expired — try again.`,
      );
    }

    await confirmClinicLogo(signed.s3Key);
  }

  function choose(file: File | undefined): void {
    if (file === undefined) return;

    // Checked here so the refusal is immediate and names the actual problem.
    // The server checks again, and the storage provider a third time, because
    // the type and size are signed into the URL.
    if (!(ALLOWED_LOGO_MIME_TYPES as readonly string[]).includes(file.type)) {
      fail("A logo must be a PNG, a JPEG or an SVG.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      fail(`That file is larger than ${String(MAX_LOGO_BYTES / 1024 / 1024)} MB.`);
      return;
    }

    // **Chosen is not uploaded.** The game dialog opens, and the upload runs
    // when it finishes — a basket, a dunk by the stick figure after a miss, or
    // Skip (STORY-179). Cancel is the one way out that uploads nothing.
    setPending(file);
  }

  /** The dialog's way out: the real upload, then close whether or not it landed. */
  async function finishLogo(file: File): Promise<void> {
    await run(() => upload(file), reload, "Logo updated.");
    // A refused upload reads as the page's own error toast, as it always did.
    setPending(null);
  }

  if (clinic === null) {
    return (
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>The clinic</h3>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.quiet}>Reading&hellip;</p>
        </div>
        {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
      </section>
    );
  }

  return (
    /*
      **The title is `h3` and says "The clinic", not "Clinic details".** The
      pane around this now carries "Practice details" as its `h2`, so repeating
      it here would print the same words twice at two sizes — and an `h2` inside
      a pane whose own heading is an `h2` breaks the document outline a screen
      reader navigates by.
    */
    <div data-testid="clinic-section">
      {/*
        **The identity, not a card row.** The clinic's name used to be one fact
        in a list of eight, under a card titled "The clinic", beside a 56px mark.
        It is the one thing on this pane that is *about* this practice, so it
        opens the pane instead: the mark at 88px, the name at display size, and
        the address it answers at underneath.
      */}
      <div className={styles.identity}>
        {clinic.logoUrl === null ? (
          <div
            className={styles.identityMarkBlank}
            data-testid="clinic-logo-blank"
            aria-hidden="true"
          >
            {initialOf(clinic.name)}
          </div>
        ) : (
          <img
            className={styles.identityMark}
            src={clinic.logoUrl}
            alt={`${clinic.name} logo`}
            data-testid="clinic-logo"
          />
        )}

        <div className={styles.identityBody}>
          <h3 className={styles.identityName} data-testid="clinic-fact-name">
            {clinic.name}
          </h3>
          <span className={styles.identitySlug}>{clinic.slug}</span>

          <div className={styles.identityActions}>
            <input
              ref={fileInput}
              type="file"
              accept={ALLOWED_LOGO_MIME_TYPES.join(",")}
              className={styles.fileInput}
              data-testid="clinic-logo-file"
              onChange={(event) => {
                choose(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => fileInput.current?.click()}
              data-testid="clinic-logo-upload"
            >
              {clinic.hasLogo ? "Replace logo" : "Upload a logo"}
            </button>
            {clinic.hasLogo && (
              <button
                type="button"
                className={styles.danger}
                disabled={busy}
                onClick={() => run(removeClinicLogo, reload, "Logo removed.")}
                data-testid="clinic-logo-remove"
              >
                Remove
              </button>
            )}
            {!editing && (
              <button
                type="button"
                className={styles.ghost}
                disabled={busy}
                onClick={() => setEditing(true)}
                data-testid="clinic-edit"
              >
                Edit details
              </button>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Edit details</h3>
          </div>
          <div className={styles.cardBody}>
            <ClinicForm
              clinic={clinic}
              busy={busy}
              onCancel={() => setEditing(false)}
              onSubmit={(body) => {
                void run(() => updateClinic(body), reload, "Saved.").then((saved) => {
                  // Closed on the write landing and on nothing else.
                  if (saved) setEditing(false);
                });
              }}
            />
          </div>
        </section>
      )}

      {/*
        ── the two groups the identity does not answer ──────────────────────
        What a new location *inherits*, and how a patient *reaches* the
        practice. The third question — what this practice *is* — is the block
        above, so its name and address left this list.

        Nothing is editable in place: one Edit opens one form, because the
        server takes one write. Hidden while editing, since that form already
        holds every field here and showing both is the screen saying the same
        thing twice with one copy stale.
      */}
      {!editing && (
        <>
          <FactGroup title="Defaults for a new location">
            <Fact label="Timezone" value={clinic.defaultTimezone} />
            <Fact label="Home currency" value={clinic.currency} testId="clinic-fact-currency" />
            {/*
              QA #5, #35 — this was read by two services, returned by the API,
              and settable by nothing, so every clinic sat at "not set" forever
              and a national phone number could never be typed.
            */}
            <Fact
              label="Country"
              value={clinic.defaultCountry ?? "Not set"}
              testId="clinic-fact-country"
            />
          </FactGroup>

          {/*
            ST5 — the day a new person's week opens on until somebody sets
            theirs. It fills the Staff editor and saves nothing by itself. Since
            QA4-D5 it is also every new location's opening hours, on these days.
          */}
          <FactGroup title="Default working day">
            <Fact
              label={daysText(clinic.defaultWorkingDays)}
              value={`${clinic.defaultShiftStartsAt}–${clinic.defaultShiftEndsAt}`}
              testId="clinic-fact-shift"
            />
          </FactGroup>

          {/*
            EB4 — phone and address live on each location now; a practice with
            one location was typing them twice. Email stays: a location has none.
          */}
          <FactGroup title="How the practice is reached">
            <Fact label="Email" value={clinic.email ?? "Not set"} />
          </FactGroup>
        </>
      )}

      {pending !== null && (
        <LogoHoopDialog
          file={pending}
          onFinish={() => finishLogo(pending)}
          onCancel={() => setPending(null)}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}

/** One card of read-only facts, with the question it answers as its title. */
function FactGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>{title}</h3>
        </div>
      </div>
      <dl className={styles.facts}>{children}</dl>
    </section>
  );
}

function Fact({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <div className={styles.fact}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.factValue} data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

/**
 * The edit form.
 *
 * **The currency input is absent, not disabled, once it is locked.** A disabled
 * field that silently drops its value on save is worse than no field: the
 * admin sees what looks like an editable form and cannot tell whether their
 * change took. The fact list above says what the currency is and why it cannot
 * move.
 */
function ClinicForm({
  clinic,
  busy,
  onCancel,
  onSubmit,
}: {
  clinic: Clinic;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: {
    name: string;
    defaultTimezone: string;
    currency?: string;
    defaultCountry: string;
    email: string;
    defaultShiftStartsAt: string;
    defaultShiftEndsAt: string;
    defaultWorkingDays: number[];
  }) => void;
}): React.JSX.Element {
  const [name, setName] = useState(clinic.name);
  const [timezone, setTimezone] = useState(clinic.defaultTimezone);
  const [currency, setCurrency] = useState(clinic.currency);
  const [country, setCountry] = useState(clinic.defaultCountry ?? "");
  const [email, setEmail] = useState(clinic.email ?? "");
  const [shiftStarts, setShiftStarts] = useState(clinic.defaultShiftStartsAt);
  const [shiftEnds, setShiftEnds] = useState(clinic.defaultShiftEndsAt);
  const [workingDays, setWorkingDays] = useState<number[]>(clinic.defaultWorkingDays);

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name: name.trim(),
          defaultTimezone: timezone.trim(),
          // Omitted entirely when locked, so a round-tripped form cannot become
          // unsaveable the moment the first price is entered.
          ...(clinic.currencyLocked ? {} : { currency: currency.trim().toUpperCase() }),
          defaultCountry: country.trim().toUpperCase(),
          email: email.trim(),
          defaultShiftStartsAt: shiftStarts,
          defaultShiftEndsAt: shiftEnds,
          defaultWorkingDays: workingDays,
        });
      }}
    >
      <div className={styles.formGrid}>
        <Field
          id="clinic-name"
          label="Name"
          value={name}
          onChange={setName}
          testId="clinic-name"
          required
        />
        {/*
          **Three dropdowns and a split phone field, replacing four text boxes.**

          Every one of these is validated strictly by the server — an IANA zone,
          an ISO 4217 code, an ISO 3166-1 alpha-2 code in capitals, an E.164
          number — so typing them from memory produced three refusals at once
          and no way to discover the right spelling. See `ui/pickers.tsx`.
        */}
        <TimezonePicker
          id="clinic-timezone"
          label="Default timezone"
          value={timezone}
          onChange={setTimezone}
          testId="clinic-timezone"
          required
        />
        {!clinic.currencyLocked && (
          <CurrencyPicker
            id="clinic-currency"
            label="Home currency"
            note="Salaries, shared expenses and supplier prices. Each location has its own, set on the location."
            value={currency}
            onChange={setCurrency}
            testId="clinic-currency"
            required
          />
        )}
        <CountryPicker
          id="clinic-country"
          label="Country"
          value={country}
          onChange={setCountry}
          testId="clinic-country"
        />
        <Field
          id="clinic-email"
          label="Email"
          value={email}
          onChange={setEmail}
          testId="clinic-email"
        />
        <div className={styles.field}>
          <label className={styles.label} htmlFor="clinic-shift-starts">
            Working day starts
            <RequiredMark />
          </label>
          <input
            id="clinic-shift-starts"
            type="time"
            className={styles.input}
            value={shiftStarts}
            onChange={(event) => setShiftStarts(event.target.value)}
            data-testid="clinic-shift-starts"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="clinic-shift-ends">
            Working day ends
            <RequiredMark />
          </label>
          <input
            id="clinic-shift-ends"
            type="time"
            className={styles.input}
            value={shiftEnds}
            onChange={(event) => setShiftEnds(event.target.value)}
            data-testid="clinic-shift-ends"
          />
        </div>
        <div className={styles.spanAll}>
          <WorkingDays value={workingDays} onChange={setWorkingDays} testId="clinic-working-days" />
        </div>
      </div>

      <div className={styles.formActions}>
        <button type="submit" className={styles.primary} disabled={busy} data-testid="clinic-save">
          Save
        </button>
        <button type="button" className={styles.ghost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  testId,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  testId: string;
  required?: boolean;
}): React.JSX.Element {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required === true && <RequiredMark />}
      </label>
      <input
        id={id}
        className={styles.input}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * "Monday to Friday", "Monday, Wednesday and Saturday", "No working days" —
 * a run of three or more consecutive days reads as a range.
 */
function daysText(days: readonly number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  const name = (d: number): string => DAY_NAMES[d - 1] ?? "";
  if (sorted.length === 0) return "No working days";
  const first = sorted[0] ?? 1;
  const last = sorted[sorted.length - 1] ?? 1;
  if (sorted.length >= 3 && last - first === sorted.length - 1) {
    return `${name(first)} to ${name(last)}`;
  }
  if (sorted.length === 1) return name(first);
  return `${sorted.slice(0, -1).map(name).join(", ")} and ${name(last)}`;
}

/** The stand-in when there is no logo. One letter, from the clinic's own name. */
function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}
